// Tier-2 <Button> backed by Ant Design (FORT-182). Screens import this, never
// `antd` directly, so the backing library can be swapped later (FORT-185)
// without touching call sites. The variant/size vocabulary is ours, not AntD's —
// that decoupling is the whole point of the primitive layer (see the ADR:
// docs/adr/0001-tier-2-primitives-library.md).
//
// Colour and radius come from the token-sourced AntD global theme (ConfigProvider
// in App.tsx / ThemedLayout), so there are no raw values here.
import React from 'react';
import { Button as AntButton } from 'antd';
import type { CommonProps } from './types';

export type ButtonVariant = 'primary' | 'default' | 'danger' | 'ghost' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  disabled?: boolean;
  /** Full-width button. */
  block?: boolean;
  icon?: React.ReactNode;
  /** Native button type. `submit` triggers the enclosing AntD Form. */
  htmlType?: 'button' | 'submit' | 'reset';
  /**
   * Accessible name. Required when the button is icon-only — without children there is
   * no text for a screen reader, and a tooltip is not an accessible name.
   */
  'aria-label'?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  children?: React.ReactNode;
}

const VARIANT_TO_ANTD: Record<
  ButtonVariant,
  { type?: 'primary' | 'default' | 'link'; danger?: boolean; ghost?: boolean }
> = {
  primary: { type: 'primary' },
  default: { type: 'default' },
  danger: { type: 'primary', danger: true },
  ghost: { type: 'default', ghost: true },
  link: { type: 'link' },
};

const SIZE_TO_ANTD: Record<ButtonSize, 'small' | 'middle' | 'large'> = {
  sm: 'small',
  md: 'middle',
  lg: 'large',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'default',
  size = 'md',
  loading,
  disabled,
  block,
  icon,
  htmlType = 'button',
  'aria-label': ariaLabel,
  onClick,
  children,
  className,
  style,
  'data-testid': dataTestId,
}) => (
  <AntButton
    {...VARIANT_TO_ANTD[variant]}
    size={SIZE_TO_ANTD[size]}
    loading={loading}
    disabled={disabled}
    block={block}
    icon={icon}
    htmlType={htmlType}
    aria-label={ariaLabel}
    onClick={onClick}
    className={className}
    style={style}
    data-testid={dataTestId}
  >
    {children}
  </AntButton>
);
